import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, Eye, EyeOff, Upload, Image } from "lucide-react";
import ImageUpload from "../../ImageUpload";
import { API, getImageUrl } from "./adminConfig";

const ContentBoxesTab = () => {
  const [contentBoxes, setContentBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBox, setEditingBox] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchContentBoxes();
  }, []);

  const fetchContentBoxes = async () => {
    try {
      const response = await axios.get(`${API}/admin/home-content-boxes`);
      setContentBoxes(response.data.content_boxes || []);
    } catch (error) {
      console.error('Error fetching content boxes:', error);
      toast.error('Error loading content boxes');
    } finally {
      setLoading(false);
    }
  };

  const updateContentBox = async (boxId, boxData) => {
    try {
      await axios.put(`${API}/admin/home-content-boxes/${boxId}`, boxData);
      toast.success('Content box updated successfully!');
      fetchContentBoxes();
      setEditingBox(null);
    } catch (error) {
      console.error('Error updating content box:', error);
      toast.error('Error updating content box');
    }
  };

  const createContentBox = async (boxData) => {
    try {
      await axios.post(`${API}/admin/home-content-boxes`, boxData);
      toast.success('Content box created successfully!');
      fetchContentBoxes();
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating content box:', error);
      toast.error('Error creating content box');
    }
  };

  const deleteContentBox = async (boxId) => {
    if (!confirm('Are you sure you want to delete this content box?')) return;
    
    try {
      await axios.delete(`${API}/admin/home-content-boxes/${boxId}`);
      toast.success('Content box deleted successfully!');
      fetchContentBoxes();
    } catch (error) {
      console.error('Error deleting content box:', error);
      toast.error('Error deleting content box');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading content boxes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Homepage Content Boxes</h2>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Content Box
        </Button>
      </div>

      <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-xs font-bold text-burgundy">📦</span>
          </div>
          <div>
            <h4 className="font-semibold text-burgundy mb-1">Homepage Content Boxes</h4>
            <p className="text-sm text-burgundy/70">
              Edit the content boxes that appear on your homepage. Keep icons as-is but customize titles and descriptions. Changes appear instantly on the homepage.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contentBoxes
          .sort((a, b) => a.order - b.order)
          .map((box) => (
            <ContentBoxCard 
              key={box.id} 
              box={box} 
              onEdit={() => setEditingBox(box)}
              onDelete={() => deleteContentBox(box.id)}
            />
          ))}

        <Card className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20 border-dashed">
          <CardContent className="flex flex-col items-center justify-center h-48 text-center">
            <LayoutGrid className="h-12 w-12 text-burgundy/30 mb-4" />
            <h3 className="text-lg font-semibold text-burgundy/60 mb-2 font-serif">Add New Content Box</h3>
            <p className="text-burgundy/50 text-sm mb-4">Create a new content box to display on your homepage.</p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Content Box
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      {editingBox && (
        <ContentBoxModal 
          box={editingBox}
          onSave={updateContentBox}
          onClose={() => setEditingBox(null)}
          mode="edit"
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <ContentBoxModal 
          onSave={createContentBox}
          onClose={() => setShowCreateModal(false)}
          mode="create"
        />
      )}
    </div>
  );
};

// Content Box Card Component
const ContentBoxCard = ({ box, onEdit, onDelete }) => {
  const getAccentColor = (accent) => {
    switch(accent) {
      case 'burgundy': return 'border-burgundy/30 bg-burgundy/5';
      case 'dark-teal': return 'border-dark-teal/30 bg-dark-teal/5';
      case 'gold': return 'border-gold/30 bg-gold/5';
      default: return 'border-burgundy/30 bg-burgundy/5';
    }
  };

  return (
    <Card className={`hover:shadow-lg transition-all duration-300 bg-white ${getAccentColor(box.accent)}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg font-serif text-burgundy">{box.title}</CardTitle>
          <Badge variant="outline" className="border-emerald-500 text-emerald-600">
            #{box.order}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-burgundy/70 text-sm mb-4 line-clamp-3">
          {box.description}
        </p>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs">
            {box.icon}
          </Badge>
          <div className="flex space-x-2">
            <Button size="sm" variant="ghost" onClick={onEdit}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Content Box Modal Component
const ContentBoxModal = ({ box, onSave, onClose, mode }) => {
  const [formData, setFormData] = useState({
    id: box?.id || '',
    title: box?.title || '',
    description: box?.description || '',
    icon: box?.icon || 'Star',
    accent: box?.accent || 'burgundy',
    order: box?.order || 1
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'edit') {
      onSave(formData.id, formData);
    } else {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy flex justify-between items-center">
            {mode === 'edit' ? 'Edit Content Box' : 'Create Content Box'}
            <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                  placeholder="Content box title"
                  required
                />
              </div>
              
              {mode === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">ID</label>
                  <Input
                    value={formData.id}
                    onChange={(e) => setFormData(prev => ({...prev, id: e.target.value}))}
                    placeholder="unique-id"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Icon</label>
                <Select 
                  value={formData.icon} 
                  onValueChange={(value) => setFormData(prev => ({...prev, icon: value}))}
                >
                  <SelectTrigger className="w-full border-burgundy/30 focus:border-gold">
                    <SelectValue placeholder="Select icon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MessageCircle">💬 MessageCircle</SelectItem>
                    <SelectItem value="BookOpen">📖 BookOpen</SelectItem>
                    <SelectItem value="Calculator">🧮 Calculator</SelectItem>
                    <SelectItem value="TrendingUp">📈 TrendingUp</SelectItem>
                    <SelectItem value="ShoppingBag">🛍️ ShoppingBag</SelectItem>
                    <SelectItem value="Star">⭐ Star</SelectItem>
                    <SelectItem value="Heart">❤️ Heart</SelectItem>
                    <SelectItem value="Sparkles">✨ Sparkles</SelectItem>
                    <SelectItem value="Calendar">📅 Calendar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Color Accent</label>
                <Select 
                  value={formData.accent} 
                  onValueChange={(value) => setFormData(prev => ({...prev, accent: value}))}
                >
                  <SelectTrigger className="w-full border-burgundy/30 focus:border-gold">
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="burgundy">🍷 Burgundy</SelectItem>
                    <SelectItem value="dark-teal">🌊 Dark Teal</SelectItem>
                    <SelectItem value="gold">✨ Gold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Display Order</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({...prev, order: parseInt(e.target.value)}))}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                placeholder="Describe what this content box offers..."
                className="min-h-[100px]"
                required
              />
            </div>

            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                {mode === 'edit' ? 'Update' : 'Create'} Content Box
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Inline Dosing Form Component

export { ContentBoxesTab, ContentBoxCard };
export default ContentBoxesTab;
